import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { ConfirmDialogComponent, UiPalette } from '@eiq/ui';

import { BlockType, BlockModel } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { SurveyConditionalsService } from './survey-conditionals.service';
import { SurveyEditorService } from './survey-editor.service';
import { Direction } from '../models/positioning/direction.type';
import { Warning } from '../models/positioning/warning.model';
import { BlockTypeContainers } from '../models/survey-editor/block-type.constants';
import { SurveyEditor } from '../models/survey-editor/survey-editor.model';

@Injectable({
  providedIn: 'root'
})
export class SurveyPositioningService {
  private surveyEditor: SurveyEditor;

  private dialog = inject(MatDialog);

  private surveyConditionalService: SurveyConditionalsService;
  private surveyEditorService: SurveyEditorService;

  init(survey: SurveyEditor, surveyConditionalService: SurveyConditionalsService, surveyEditorService: SurveyEditorService): void {
    this.surveyConditionalService = surveyConditionalService;
    this.surveyEditorService = surveyEditorService;
    this.surveyEditor = survey;
  }

  move(direction: Direction, questionId: string): void {
    const { currentIndex, newIndex } = this.mapIndex(direction, questionId);

    if (currentIndex === -1 || newIndex === -1) {
      this.moveOnBlocks(questionId, null, direction);
      this.surveyEditor.amendPages();
      this.surveyEditorService.refreshQuestionsList();
      return; // Invalid indexes, cannot proceed
    }

    const warnings = this.mapWarnings(currentIndex, newIndex, questionId);
    if (warnings.length === 0) {
      this.moveQuestion(direction, questionId, currentIndex, newIndex);
      return;
    }

    const blockers = warnings.filter((w) => w.blockerInd);

    if (blockers.length > 0) {
      this.showConfirmation(blockers, 'warn');
      return;
    }

    const confirmationRef = this.showConfirmation(warnings, 'alert');

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.moveQuestion(direction, questionId, currentIndex, newIndex);
      }
    });
  }

  forceMove(
    questionId: string,
    currentIndex: number,
    swapQuestionId: string,
    toTheEnd: boolean,
    movingIntoBlock: boolean,
    targetBlock: BlockModel,
    isBeginningOfBlock: boolean,
    isEndOfBlock: boolean
  ): boolean {
    if (movingIntoBlock && targetBlock && BlockTypeContainers.includes(targetBlock.type)) {
      this.showConfirmation([new Warning('Choose a child block to move questions into. Questions cannot be moved directly into a parent.', true)], 'warn');
      return false;
    }
    let newIndex = toTheEnd ? this.surveyEditor.questions.length - 1 : this.surveyEditor.questions.findIndex((q: Question) => q.id === swapQuestionId);

    if (!toTheEnd && newIndex > currentIndex) {
      newIndex--;
    }

    if (isEndOfBlock) {
      // Index is increased. Instead of swapping position with targetQuestion, we want to place it after the target
      newIndex++;
    }

    const affectedQuestionConditions = this.surveyConditionalService.affectedQuestionConditionsByMove(currentIndex, newIndex);
    this.surveyConditionalService.removeAllAffectedConditionsByMove(questionId, affectedQuestionConditions);

    if (!movingIntoBlock) {
      const sourceBlock = this.surveyEditor.blocks.byGroupedQId(questionId);

      // If the question is moved out of any block, remove it from the block form
      if (sourceBlock && !targetBlock) {
        this.surveyEditor.blocks.removeQuestionFromBlock(
          sourceBlock.id,
          sourceBlock.groupedQuestionIds.findIndex((id) => id === questionId)
        );
      }

      this.surveyEditor.moveQuestion(currentIndex, newIndex);

      this.surveyEditor.blocks.amendBlocks();
      this.surveyEditor.blocks.refreshLists();
    } else {
      this.forceMoveOnBlocks(questionId, swapQuestionId, targetBlock, isBeginningOfBlock, isEndOfBlock);

      const updatedCurrentIndex = this.surveyEditor.questions.findIndex((q: Question) => q.id === questionId);
      const currentBlock = this.surveyEditorService
        .surveyEditor$()
        .blocks.all$()
        .find((b) => b.id === targetBlock.id);
      const isFirstInBlockAndUnmoved = currentBlock?.groupedQuestionIds[0] === questionId && updatedCurrentIndex === newIndex;

      if (!isFirstInBlockAndUnmoved && updatedCurrentIndex !== newIndex) {
        this.surveyEditor.moveQuestion(updatedCurrentIndex, newIndex);
      }
    }

    this.surveyEditor.amendPages();
    this.surveyEditorService.refreshQuestionsList();
    return true;
  }

  checkWarnings(newIndex: number, questionId: string): Warning[] {
    const currentIndex = this.surveyEditorService.surveyEditor$().questions.findIndex((q: Question) => q.id === questionId);
    return this.mapWarnings(currentIndex, newIndex, questionId);
  }

  getAllDependenciesOfQuestions(questionIdSet: Set<string>): {
    selectedQuestions: Question[];
    affectedQuestions: Set<Question>;
    affectedBlocks: Set<BlockModel>;
  } {
    const selectedQuestions = this.surveyEditor.questions.filter((q) => questionIdSet.has(q.id));

    const affectedQuestions = new Set<Question>();
    const affectedBlocks = new Set<BlockModel>();

    selectedQuestions.forEach((question) => {
      const { questions, blocks } = this.getAffectedDependencies(question.id);

      questions.forEach((q) => {
        if (!questionIdSet.has(q.id)) {
          affectedQuestions.add(q);
        }
      });

      blocks.forEach((b) => affectedBlocks.add(b));
    });
    return { selectedQuestions, affectedQuestions, affectedBlocks };
  }

  private getAffectedDependencies(questionId: string): { questions: Question[]; blocks: BlockModel[] } {
    const editor = this.surveyEditor;
    return {
      questions: [
        ...editor.affectedQuestionsOnConditionalOfSelectedQuestion(questionId),
        ...editor.affectedHiddenQuestionsOnConditionalOfSelectedQuestion(questionId)
      ],
      blocks: editor.blocks.affectedConditions(questionId).map((blockId) => editor.blocks.byId(blockId))
    };
  }

  private moveQuestion(direction: Direction, movingQuestionId: string, currentIndex: number, newIndex: number): void {
    const movingQuestion = this.surveyEditor.questions[currentIndex];
    const targetQuestionId = this.surveyEditor.questions[newIndex].id;
    const movingQuestionBlock = this.findQuestionBlock(movingQuestion.id);
    const targetQuestionBlock = this.findQuestionBlock(targetQuestionId);

    const movingQuestionBeforeBlock = !targetQuestionBlock && !!movingQuestionBlock && direction === 'up';
    if (!movingQuestionBeforeBlock) {
      this.updateQuestionConditions(movingQuestionId, currentIndex, newIndex);
    }
    const freshMoveOutOfBlock = this.moveOnBlocks(movingQuestionId, targetQuestionId, direction);

    if (!freshMoveOutOfBlock) {
      this.executeQuestionMove(currentIndex, newIndex, movingQuestionBlock);
    }

    this.surveyEditor.amendPages();
    this.surveyEditorService.refreshQuestionsList();
  }

  private forceMoveOnBlocks(
    movingQuestionId: string,
    targetQuestionId: string | undefined,
    targetBlock: BlockModel,
    isBlockBeginning: boolean = false,
    isBlockEnd: boolean = false
  ): boolean {
    const sourceBlock = this.surveyEditor.blocks.byGroupedQId(movingQuestionId);
    const movedOutOfBlock = (sourceBlock && !targetBlock) || (sourceBlock && targetBlock && sourceBlock.id !== targetBlock.id);

    if (movedOutOfBlock) {
      this.surveyEditor.blocks.removeQuestionFromBlock(
        sourceBlock.id,
        sourceBlock.groupedQuestionIds.findIndex((id) => id === movingQuestionId)
      );
    } else {
      this.handleForceMovementFromCurrentBlock(movingQuestionId, targetQuestionId, sourceBlock, targetBlock, isBlockBeginning, isBlockEnd);
    }

    if (sourceBlock) {
      const isInsideMultiBlock = this.isBlockInsideMultiBlock(sourceBlock.id);
      const isSourceLastBlockInMultiBlock = this.isLastBlockInsideMultiBlock(sourceBlock.id);

      if (
        targetQuestionId &&
        this.shouldInsertIntoNewBlock(movedOutOfBlock, isInsideMultiBlock, isSourceLastBlockInMultiBlock, targetQuestionId, targetBlock, sourceBlock)
      ) {
        this.handleForceMovementIntoNewBlock(movingQuestionId, targetQuestionId, targetBlock, sourceBlock.id, isBlockBeginning, isBlockEnd);
      }
    } else if (targetQuestionId) {
      // Case: The moving question was already outside a block and needs to be placed next to a target outside of a block.
      this.handleForceMovementIntoNewBlock(movingQuestionId, targetQuestionId, targetBlock, undefined, isBlockBeginning, isBlockEnd);
    }

    this.surveyEditor.blocks.amendBlocks();
    this.surveyEditor.blocks.refreshLists();

    return movedOutOfBlock;
  }

  private moveOnBlocks(movingQuestionId: string, targetQuestionId: string | undefined, direction: Direction): boolean {
    const sourceBlock = this.surveyEditor.blocks.byGroupedQId(movingQuestionId);
    const movedOutOfBlock = sourceBlock ? this.handleMovementFromCurrentBlock(movingQuestionId, sourceBlock, direction) : false;

    if (sourceBlock) {
      const isInsideMultiBlock = this.isBlockInsideMultiBlock(sourceBlock.id);
      const isLastBlockInMultiBlock = this.isLastBlockInsideMultiBlock(sourceBlock.id);
      const targetBlock = this.surveyEditor.blocks.byGroupedQId(targetQuestionId);

      if (
        targetQuestionId &&
        this.shouldInsertIntoNewBlock(movedOutOfBlock, isInsideMultiBlock, isLastBlockInMultiBlock, targetQuestionId, targetBlock, sourceBlock)
      ) {
        this.handleMovementIntoNewBlock(movingQuestionId, targetQuestionId, sourceBlock.id, direction);
      }
    } else if (targetQuestionId) {
      // Case: The moving question was already outside a block and needs to be placed next to a target.
      this.handleMovementIntoNewBlock(movingQuestionId, targetQuestionId, undefined, direction);
    }

    this.surveyEditor.blocks.amendBlocks();
    this.surveyEditor.blocks.refreshLists();

    return movedOutOfBlock;
  }

  private showConfirmation(warning: Warning[], color: UiPalette): MatDialogRef<ConfirmDialogComponent> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Move Question',
        confirmText: warning.map((w) => w.message).join('<br>'),
        color,
        confirmBtn: color === 'warn' ? null : 'Move',
        cancelBtn: color === 'warn' ? 'Close' : 'Cancel'
      }
    });
  }

  private mapIndex(direction: Direction, questionId: string): { currentIndex: number; newIndex: number } {
    const currentIndex = this.surveyEditor.getQuestionIndex(questionId);
    if (currentIndex === -1) {
      return { currentIndex: -1, newIndex: -1 };
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= this.surveyEditor.questions.length) {
      return { currentIndex: -1, newIndex: -1 };
    }

    return { currentIndex, newIndex };
  }

  private mapWarnings(currentIndex: number, newIndex: number, questionId: string): Warning[] {
    const sourceBlock = this.surveyEditor.blocks.byGroupedQId(questionId);
    const targetQuestion = this.surveyEditor.questions[newIndex];
    let targetBlock;

    if (targetQuestion) {
      targetBlock = this.surveyEditor.blocks.byGroupedQId(targetQuestion.id);
    }

    const movingOutOfBlock = !targetBlock && sourceBlock;

    const warnings: Warning[] = [];
    if (this.invalidatesSourceOnLoopBlock(newIndex, questionId) && !movingOutOfBlock) {
      warnings.push(new Warning('This question is used as source on a loop block. Cannot be moved past the questions inside the loop.', true));
    }

    const affectedQuestionConditions = this.surveyConditionalService.affectedQuestionConditionsByMove(currentIndex, movingOutOfBlock ? currentIndex : newIndex);
    if (affectedQuestionConditions.other.length > 0) {
      warnings.push(
        new Warning(
          `The question moved is used as a conditional setting on <strong>${affectedQuestionConditions.other.join(', ')}</strong>. If continue the condition will be removed.`
        )
      );
    }
    if (affectedQuestionConditions.current.length > 0) {
      warnings.push(
        new Warning(
          `Moving this question will invalidate the conditions on <strong>${affectedQuestionConditions.current.join(', ')}</strong>. If continue the condition will be removed.`
        )
      );
    }
    if (affectedQuestionConditions.blocks.length > 0) {
      warnings.push(
        new Warning(
          `Moving this question will invalidate the conditions on the blocks: <strong>${affectedQuestionConditions.blocks.map((b) => b.blockId).join(', ')}</strong>. If continue the condition will be removed.`
        )
      );
    }

    return warnings;
  }

  private invalidatesSourceOnLoopBlock(newIndex: number, questionId: string): boolean {
    const loopBlock = this.surveyEditor.blocks.allLoops.find((block) => block.value.sourceQId === questionId);

    if (!loopBlock) {
      return false;
    }

    const blockQuestionsIndexes: number[] = [];

    loopBlock.value.groupedQuestionIds.forEach((qId) => {
      blockQuestionsIndexes.push(this.surveyEditor.questions.findIndex((q) => q.id === qId));
    });
    const lowestNumber = Math.min(...blockQuestionsIndexes);

    return lowestNumber <= newIndex;
  }

  // --- Helper Functions --- moveQuestion

  private updateQuestionConditions(questionId: string, currentIndex: number, newIndex: number): void {
    const affectedQuestionConditions = this.surveyConditionalService.affectedQuestionConditionsByMove(currentIndex, newIndex);
    this.surveyConditionalService.removeAllAffectedConditionsByMove(questionId, affectedQuestionConditions);
  }

  private findQuestionBlock(questionId: string): BlockModel | null {
    return this.surveyEditor.blocks.byGroupedQId(questionId);
  }

  private executeQuestionMove(currentIndex: number, newIndex: number, movingQuestionBlock: BlockModel | null): void {
    const targetQuestion = this.surveyEditor.questions[newIndex];
    const targetQuestionBlock = this.findQuestionBlock(targetQuestion.id);

    const isMoveWithinSameBlock = targetQuestionBlock && movingQuestionBlock && targetQuestionBlock.id === movingQuestionBlock.id;
    const bothAreNotInBlocks = !targetQuestionBlock && !movingQuestionBlock;

    if (isMoveWithinSameBlock) {
      this.surveyEditor.moveQuestion(currentIndex, newIndex);
      return;
    }

    if (bothAreNotInBlocks) {
      this.surveyEditor.moveQuestion(currentIndex, newIndex);
    }
  }

  // --- Helper Functions --- moveOnBlocks

  private handleForceMovementFromCurrentBlock(
    movingQuestionId: string,
    targetQuestionId: string,
    sourceBlock: BlockModel,
    targetBlock: BlockModel,
    isBlockBeginning: boolean = false,
    isBlockEnd: boolean = false
  ): boolean {
    if (sourceBlock && targetBlock && sourceBlock.id === targetBlock.id) {
      if (isBlockBeginning) {
        this.surveyEditor.blocks.moveQuestionToEdge(sourceBlock.id, movingQuestionId, 'start');
        return true;
      }
      if (isBlockEnd) {
        this.surveyEditor.blocks.moveQuestionToEdge(sourceBlock.id, movingQuestionId, 'end');
        return true;
      }
      this.surveyEditor.blocks.swapPositionById(movingQuestionId, targetQuestionId, sourceBlock.id);
      return true;
    }
  }

  private handleMovementFromCurrentBlock(movingQuestionId: string, block: BlockModel, direction: Direction): boolean {
    const groupedIds = block.groupedQuestionIds;
    const isFirstOnBlock = groupedIds[0] === movingQuestionId;
    const isLastOnBlock = groupedIds[groupedIds.length - 1] === movingQuestionId;

    if (isLastOnBlock && direction === 'down') {
      this.surveyEditor.blocks.removeLastQuestionFromBlock(block.id);
      return true;
    } else if (isFirstOnBlock && direction === 'up') {
      this.surveyEditor.blocks.removeQuestionFromBlock(block.id, 0);
      return true;
    } else {
      this.surveyEditor.blocks.swapPosition(direction, block.id, movingQuestionId);
      return false;
    }
  }

  private handleMovementIntoNewBlock(movingQuestionId: string, targetQuestionId: string, originalBlockId: string | undefined, direction: Direction): void {
    const targetBlock = this.surveyEditor.blocks.byGroupedQId(targetQuestionId);

    if (targetBlock && targetBlock.id !== originalBlockId) {
      const blockControl = this.surveyEditor.blocks.all.controls.find((b: any) => b.value.id === targetBlock.id)?.get('groupedQuestionIds') as
        FormArray | undefined;

      if (!blockControl) {
        return;
      }

      const newQuestionControl = new FormControl(movingQuestionId);

      if (direction === 'up') {
        blockControl.push(newQuestionControl);
      } else {
        blockControl.insert(0, newQuestionControl);
      }
    }
  }

  private handleForceMovementIntoNewBlock(
    movingQuestionId: string,
    targetQuestionId: string,
    targetBlock: BlockModel | undefined,
    originalBlockId: string | undefined,
    isBlockBeginning: boolean,
    isBlockEnd: boolean
  ): void {
    if (targetBlock && targetBlock.id !== originalBlockId) {
      const blockControl = this.surveyEditor.blocks.all.controls.find((b: any) => b.value.id === targetBlock.id)?.get('groupedQuestionIds') as
        FormArray | undefined;

      if (!blockControl) {
        return;
      }

      const newQuestionControl = new FormControl(movingQuestionId);
      const groupedIds = blockControl.value as string[];
      const targetQuestionIndex = groupedIds.findIndex((id) => id === targetQuestionId);

      if (isBlockEnd) {
        blockControl.push(newQuestionControl);
        return;
      }
      if (isBlockBeginning) {
        blockControl.insert(0, newQuestionControl);
        return;
      }

      // Default case, insert before the target question
      if (targetQuestionIndex >= 0) {
        blockControl.insert(targetQuestionIndex, newQuestionControl);
        return;
      }
    }
  }

  private shouldInsertIntoNewBlock(
    movedOutOfBlock: boolean,
    isSourceBlockInsideMultiBlock: boolean,
    isSourceLastBlockInMultiBlock: boolean,
    targetQuestionId: string | undefined,
    targetBlock: BlockModel | undefined,
    sourceBlock: BlockModel | undefined
  ): boolean {
    let multiBlockHasTargetAndSource;
    if (targetBlock && sourceBlock && targetBlock.id !== sourceBlock.id) {
      multiBlockHasTargetAndSource = this.surveyEditor.blocks
        .all$()
        .some((b) => b.groupedBlockIds.includes(sourceBlock.id) && b.groupedBlockIds.includes(targetBlock.id));
    }

    if (targetBlock && sourceBlock && isSourceBlockInsideMultiBlock && multiBlockHasTargetAndSource && movedOutOfBlock && isSourceLastBlockInMultiBlock) {
      // Special case: moving the last block of a multiBlock to a target inside the same multiBlock but different block
      return true; // Stop checking and do insert.
    }

    if (movedOutOfBlock && targetBlock && sourceBlock && targetBlock.id !== sourceBlock.id) {
      // Moved out of block to a different target, can be block or multiBlock
      return true; // Stop checking and do not insert.
    }

    if (isSourceBlockInsideMultiBlock && isSourceLastBlockInMultiBlock && targetBlock) {
      // moving the last block of a multiBlock to a target inside the same multiBlock
      return false; // Stop checking and do not insert.
    }

    // General case:
    return (!movedOutOfBlock || isSourceBlockInsideMultiBlock) && !!targetQuestionId;
  }

  private isBlockInsideMultiBlock(blockId: string): boolean {
    return this.surveyEditor.blocks.all$().some((b) => b.type === BlockType.MultiBlock && b.groupedBlockIds.includes(blockId));
  }

  private isLastBlockInsideMultiBlock(blockId: string): boolean {
    return this.surveyEditor.blocks.all$().some((b) => b.groupedBlockIds[b.groupedBlockIds.length - 1] !== blockId);
  }
}
